import { getPricingConfig } from '@editor/global/pricingConfig';
import {
  useSharedConfigStore,
  sharedConfigStoreSelector,
} from '@editor/stores/sharedConfigStore';
import { useGrowthBook } from '@growthbook/growthbook-react';
import type { SubscriptionPrices } from '@repo/subscription/api';
import {
  useSubscriptionStore,
  subscriptionStoreSelector,
} from '@repo/subscription/client';

/**
 * Hook that retrieves and configures pricing information based on various conditions.
 * Used in subscription flows to determine pricing display and trial availability.
 *
 * @param {boolean} isFreeTrialAvailable - Whether a free trial is available for the user
 * @param {boolean} selectedExpertAddon - Whether expert addon features are selected
 * @param {SubscriptionPrice[]} subscriptionPrices - Array of available subscription prices
 *
 * @returns {ReturnType<typeof getPricingConfig> | null} Configuration object for pricing display or null if prices aren't available
 *
 * @example
 * ```tsx
 * const pricingConfig = usePricingConfig(
 *   freeTrial.isAvailable,
 *   !!selectedAddon?.generationCredits,
 *   subscriptionPrices
 * );
 * ```
 */
export const usePricingConfig = (
  isFreeTrialAvailable: boolean,
  selectedExpertAddon: boolean,
  subscriptionPrices: SubscriptionPrices
): ReturnType<typeof getPricingConfig> | null => {
  const growthBook = useGrowthBook();
  const country = useSubscriptionStore(
    subscriptionStoreSelector.requestCountry
  );

  const creditLimits = useSharedConfigStore(
    sharedConfigStoreSelector.creditLimits
  );

  if (!subscriptionPrices || subscriptionPrices.length === 0) {
    return null;
  }

  const config = getPricingConfig({
    growthBook,
    creditLimits,
    selectedExpertAddon,
    subscriptionPrices,
    country,
  });

  if (isFreeTrialAvailable) {
    config.PRO.buttonLabel = 'Start Free Trial';
    config.EXPERT.buttonLabel = 'Start Free Trial';
  }
  return config;
};
